/**
 * Location names scraped from HTML sometimes still contain entities
 * ("Albany&agrave;", "Mand&oslash;"). The old slugify treated every non-ASCII
 * character as a word break, so "Astronómico" became "astron-mico".
 *
 * Decode entities, fold letters that NFD does not decompose (ø, æ, …),
 * then NFD-strip diacritics and slugify to ASCII.
 */

/** HTML 4 Latin-1 named entities, plus the ones this dataset actually scraped. */
const NAMED_ENTITIES: Record<string, string> = {
  agrave: 'à', Agrave: 'À', aacute: 'á', Aacute: 'Á', acirc: 'â', Acirc: 'Â',
  atilde: 'ã', Atilde: 'Ã', auml: 'ä', Auml: 'Ä', aring: 'å', Aring: 'Å',
  aelig: 'æ', AElig: 'Æ', ccedil: 'ç', Ccedil: 'Ç',
  egrave: 'è', Egrave: 'È', eacute: 'é', Eacute: 'É', ecirc: 'ê', Ecirc: 'Ê', euml: 'ë', Euml: 'Ë',
  igrave: 'ì', Igrave: 'Ì', iacute: 'í', Iacute: 'Í', icirc: 'î', Icirc: 'Î', iuml: 'ï', Iuml: 'Ï',
  ntilde: 'ñ', Ntilde: 'Ñ',
  ograve: 'ò', Ograve: 'Ò', oacute: 'ó', Oacute: 'Ó', ocirc: 'ô', Ocirc: 'Ô',
  otilde: 'õ', Otilde: 'Õ', ouml: 'ö', Ouml: 'Ö', oslash: 'ø', Oslash: 'Ø',
  ugrave: 'ù', Ugrave: 'Ù', uacute: 'ú', Uacute: 'Ú', ucirc: 'û', Ucirc: 'Û', uuml: 'ü', Uuml: 'Ü',
  yacute: 'ý', Yacute: 'Ý', yuml: 'ÿ',
  szlig: 'ß', thorn: 'þ', Thorn: 'Þ', eth: 'ð', ETH: 'Ð',
  amp: '&', quot: '"', apos: "'", nbsp: ' ',
};

/**
 * Letters whose NFD form is still a single non-ASCII character.
 * ø/æ/ł/ß and the rest would otherwise be dropped as punctuation.
 */
const UNDECOMPOSED_LATIN: Record<string, string> = {
  ø: 'o', Ø: 'O',
  æ: 'ae', Æ: 'AE',
  ł: 'l', Ł: 'L',
  đ: 'd', Đ: 'D',
  ð: 'd', Ð: 'D',
  þ: 'th', Þ: 'Th',
  œ: 'oe', Œ: 'OE',
  ß: 'ss',
  ı: 'i',
  ŋ: 'n', Ŋ: 'N',
};

const APOSTROPHES = /['\u2018\u2019\u02BC]/g;
const NAMED_ENTITY = /&[a-zA-Z][a-zA-Z0-9]+;/;
/** Accent characters that the old slugify turned into a hyphen inside a word. */
const GARBLED_FRAGMENT = /astron-mico|(?:^|-)(?:agrave|oslash|aacute|eacute|iacute|oacute|uacute|ntilde)(?:-|$)/;

export interface LegacyLocationSlug {
  /** Scraped name, entities included when that is what was stored. */
  name: string;
  from: string;
  to: string;
}

/**
 * Slugs published from entity- or accent-garbled names. Each old URL 301s to `to`.
 * Albanyà keeps its grave accent in `name`; the slug folds it.
 */
export const LEGACY_LOCATION_SLUGS: readonly LegacyLocationSlug[] = [
  {
    name: 'Aigüestortes Starlight Reserve and Starlight Tourism Destination',
    from: 'aig-estortes-starlight-reserve-and-starlight-tourism-destination',
    to: 'aiguestortes-starlight-reserve-and-starlight-tourism-destination',
  },
  {
    name: 'Albany&agrave; International Dark Sky Park',
    from: 'albany-agrave-international-dark-sky-park',
    to: 'albanya-international-dark-sky-park',
  },
  {
    name: 'Bükk Starry Sky Park/International Dark Sky Park',
    from: 'b-kk-starry-sky-park-international-dark-sky-park',
    to: 'bukk-starry-sky-park-international-dark-sky-park',
  },
  {
    name: 'Cabañeros Starlight Tourism Destination',
    from: 'caba-eros-starlight-tourism-destination',
    to: 'cabaneros-starlight-tourism-destination',
  },
  {
    name: 'Centro Astronómico Son Bí Stellar Park',
    from: 'centro-astron-mico-son-b-stellar-park',
    to: 'centro-astronomico-son-bi-stellar-park',
  },
  {
    name: 'Centro Astronómico Tiedra Stellar Park',
    from: 'centro-astron-mico-tiedra-stellar-park',
    to: 'centro-astronomico-tiedra-stellar-park',
  },
  {
    name: 'Centro Interpretación Cielo Gorafe Stellar Park',
    from: 'centro-interpretaci-n-cielo-gorafe-stellar-park',
    to: 'centro-interpretacion-cielo-gorafe-stellar-park',
  },
  {
    name: 'Cévennes International Dark Sky Reserve',
    from: 'c-vennes-international-dark-sky-reserve',
    to: 'cevennes-international-dark-sky-reserve',
  },
  {
    name: 'Comarca Mancha Júcar-Centro Starlight Tourism Destination',
    from: 'comarca-mancha-j-car-centro-starlight-tourism-destination',
    to: 'comarca-mancha-jucar-centro-starlight-tourism-destination',
  },
  {
    name: 'Comarca Sierra Sur de Jaén Starlight Reserve and Starlight Tourism Destination',
    from: 'comarca-sierra-sur-de-ja-n-starlight-reserve-and-starlight-tourism-destination',
    to: 'comarca-sierra-sur-de-jaen-starlight-reserve-and-starlight-tourism-destination',
  },
  {
    name: 'Concello de Lalín Starlight Tourism Destination',
    from: 'concello-de-lal-n-starlight-tourism-destination',
    to: 'concello-de-lalin-starlight-tourism-destination',
  },
  {
    name: 'El Cerrajón de Murtas Starlight Wilderness',
    from: 'el-cerraj-n-de-murtas-starlight-wilderness',
    to: 'el-cerrajon-de-murtas-starlight-wilderness',
  },
  {
    name: 'El Chorrerón Starlight Wilderness',
    from: 'el-chorrer-n-starlight-wilderness',
    to: 'el-chorreron-starlight-wilderness',
  },
  {
    name: 'El Jabalón Stellar Park',
    from: 'el-jabal-n-stellar-park',
    to: 'el-jabalon-stellar-park',
  },
  {
    name: 'Faro de Avión (Covelo) Starlight Wilderness',
    from: 'faro-de-avi-n-covelo-starlight-wilderness',
    to: 'faro-de-avion-covelo-starlight-wilderness',
  },
  {
    name: 'Gúdar Javalambre Starlight Reserve and Starlight Tourism Destination',
    from: 'g-dar-javalambre-starlight-reserve-and-starlight-tourism-destination',
    to: 'gudar-javalambre-starlight-reserve-and-starlight-tourism-destination',
  },
  {
    name: 'Hortobágy Starry-Sky Park/International Dark Sky Park',
    from: 'hortob-gy-starry-sky-park-international-dark-sky-park',
    to: 'hortobagy-starry-sky-park-international-dark-sky-park',
  },
  {
    name: 'Iberá Starlight Reserve and Starlight Tourism Destination',
    from: 'iber-starlight-reserve-and-starlight-tourism-destination',
    to: 'ibera-starlight-reserve-and-starlight-tourism-destination',
  },
  {
    name: 'Isla de Tabarca - Punta Falcón Starlight Wilderness',
    from: 'isla-de-tabarca-punta-falc-n-starlight-wilderness',
    to: 'isla-de-tabarca-punta-falcon-starlight-wilderness',
  },
  {
    name: 'Islas Cíes Camping Starlight Camp',
    from: 'islas-c-es-camping-starlight-camp',
    to: 'islas-cies-camping-starlight-camp',
  },
  {
    name: 'Jardin Botánico de Santa Catalina Stellar Park',
    from: 'jardin-bot-nico-de-santa-catalina-stellar-park',
    to: 'jardin-botanico-de-santa-catalina-stellar-park',
  },
  {
    // ERI-2857 renamed it to the Starlight spelling, "del Cigüela".
    name: 'La Estela del Cigüela Starlight Walking Trail',
    from: 'la-estela-de-cig-ela-starlight-walking-trail',
    to: 'la-estela-del-ciguela-starlight-walking-trail',
  },
  {
    name: 'Lerín Village Starlight Tourism Destination',
    from: 'ler-n-village-starlight-tourism-destination',
    to: 'lerin-village-starlight-tourism-destination',
  },
  {
    name: 'Mand&oslash; International Dark Sky Park',
    from: 'mand-oslash-international-dark-sky-park',
    to: 'mando-international-dark-sky-park',
  },
  {
    name: 'Mariñas Coruñesas Starlight Tourism Destination',
    from: 'mari-as-coru-esas-starlight-tourism-destination',
    to: 'marinas-corunesas-starlight-tourism-destination',
  },
  {
    name: 'Monfragüe Starlight Tourism Destination',
    from: 'monfrag-e-starlight-tourism-destination',
    to: 'monfrague-starlight-tourism-destination',
  },
  {
    name: 'Mont-Mégantic International Dark Sky Reserve',
    from: 'mont-m-gantic-international-dark-sky-reserve',
    to: 'mont-megantic-international-dark-sky-reserve',
  },
  {
    name: 'M&oslash;n and Nyord International Dark Sky Community',
    from: 'm-oslash-n-and-nyord-international-dark-sky-community',
    to: 'mon-and-nyord-international-dark-sky-community',
  },
  {
    name: 'M&oslash;n and Nyord International Dark Sky Park',
    from: 'm-oslash-n-and-nyord-international-dark-sky-park',
    to: 'mon-and-nyord-international-dark-sky-park',
  },
  {
    name: 'Observatorio Astronómico de la Hita Starlight Stellar Park',
    from: 'observatorio-astron-mico-de-la-hita-starlight-stellar-park',
    to: 'observatorio-astronomico-de-la-hita-starlight-stellar-park',
  },
  {
    name: '&Oslash;vre Pasvik National Park International Dark Sky Park',
    from: 'oslash-vre-pasvik-national-park-international-dark-sky-park',
    to: 'ovre-pasvik-national-park-international-dark-sky-park',
  },
  {
    name: 'Parque Nacional das Illas Atlánticas de Galicia Starlight Tourism Destination',
    from: 'parque-nacional-das-illas-atl-nticas-de-galicia-starlight-tourism-destination',
    to: 'parque-nacional-das-illas-atlanticas-de-galicia-starlight-tourism-destination',
  },
  {
    name: 'Pueblo de Fuente Tójar Starlight Tourism Destination',
    from: 'pueblo-de-fuente-t-jar-starlight-tourism-destination',
    to: 'pueblo-de-fuente-tojar-starlight-tourism-destination',
  },
  {
    name: 'Pueblo de Las Navas del Marqués Starlight Tourism Destination',
    from: 'pueblo-de-las-navas-del-marqu-s-starlight-tourism-destination',
    to: 'pueblo-de-las-navas-del-marques-starlight-tourism-destination',
  },
  {
    name: 'Pueblo de San Andrés de Pica Starlight Tourism Destination',
    from: 'pueblo-de-san-andr-s-de-pica-starlight-tourism-destination',
    to: 'pueblo-de-san-andres-de-pica-starlight-tourism-destination',
  },
  {
    name: 'Rhön International Dark Sky Reserve',
    from: 'rh-n-international-dark-sky-reserve',
    to: 'rhon-international-dark-sky-reserve',
  },
  {
    name: 'Santa Lucía de Tirajana Starlight Tourism Destination',
    from: 'santa-luc-a-de-tirajana-starlight-tourism-destination',
    to: 'santa-lucia-de-tirajana-starlight-tourism-destination',
  },
  {
    name: 'Serranía de Cuenca Starlight Tourism Destination',
    from: 'serran-a-de-cuenca-starlight-tourism-destination',
    to: 'serrania-de-cuenca-starlight-tourism-destination',
  },
  {
    name: 'Sierra de Albarracín Starlight Reserve and Starlight Tourism Destination',
    from: 'sierra-de-albarrac-n-starlight-reserve-and-starlight-tourism-destination',
    to: 'sierra-de-albarracin-starlight-reserve-and-starlight-tourism-destination',
  },
  {
    name: 'Sierra de Cádiz Starlight Tourism Destination',
    from: 'sierra-de-c-diz-starlight-tourism-destination',
    to: 'sierra-de-cadiz-starlight-tourism-destination',
  },
  {
    name: 'Tähuna Glenorchy International Dark Sky Sanctuary',
    from: 't-huna-glenorchy-international-dark-sky-sanctuary',
    to: 'tahuna-glenorchy-international-dark-sky-sanctuary',
  },
  {
    name: 'Torralba del Río Starlight Tourism Destination',
    from: 'torralba-del-r-o-starlight-tourism-destination',
    to: 'torralba-del-rio-starlight-tourism-destination',
  },
  {
    name: 'Tumacácori National Historical Park International Dark Sky Park',
    from: 'tumac-cori-national-historical-park-international-dark-sky-park',
    to: 'tumacacori-national-historical-park-international-dark-sky-park',
  },
  {
    // ERI-2857: "Volová" was one village's name for the Poloniny Dark Sky Park.
    name: 'Poloniny Dark Sky Park',
    from: 'volov',
    to: 'poloniny-dark-sky-park',
  },
];

export function decodeHtmlEntities(value: string): string {
  let current = value;
  for (let pass = 0; pass < 2; pass++) {
    const next = current
      .replace(/&#x([0-9a-fA-F]+);/g, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(parseInt(dec, 10)))
      .replace(/&([a-zA-Z]+);/g, (match, name: string) => NAMED_ENTITIES[name] ?? match);
    if (next === current) break;
    current = next;
  }
  return current;
}

function foldUndecomposedLatin(value: string): string {
  let out = '';
  for (const ch of value) out += UNDECOMPOSED_LATIN[ch] ?? ch;
  return out;
}

/** ASCII slug: decoded, accent-folded, lowercase, at most 120 characters. */
export function slugifyLocationName(name: string): string {
  const folded = foldUndecomposedLatin(decodeHtmlEntities(name));
  const ascii = folded.normalize('NFD').replace(/\p{M}/gu, '');
  return ascii
    .toLowerCase()
    .replace(APOSTROPHES, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 120);
}

/**
 * Undo the escapes a rendered title uses (`&amp;`, `&#x27;`, numeric code
 * points) so a leftover named entity such as `&agrave;` still shows up.
 */
export function unescapeRenderedText(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&apos;|&#0*39;|&#x0*27;/gi, "'")
    .replace(/&#x([0-9a-fA-F]+);/gi, (_, hex: string) => String.fromCodePoint(parseInt(hex, 16)))
    .replace(/&#(\d+);/g, (_, dec: string) => String.fromCodePoint(parseInt(dec, 10)));
}

/** Why a title or slug is still garbled, or null when it is clean. */
export function garbledLocationText(value: string): 'html entity' | 'garbled fragment' | null {
  const text = unescapeRenderedText(value);
  if (NAMED_ENTITY.test(text)) return 'html entity';
  if (GARBLED_FRAGMENT.test(text)) return 'garbled fragment';
  return null;
}
